import { useIsGuestEmailExistingMember } from '#src/app/shared/hooks/membership';
import type { PaymentType } from '#src/app/shared/utils/payments';

import { usePromoteFormData } from '../../../formContext/formData';
import { useEnforceMembershipAmount } from '../useEnforceMembershipAmount';
import { useIsMembershipEligible } from '../useIsMembershipEligible';

import { useNavigateToPpmuPage } from './hooks/useNavigateToPpmuPage';
import { useTokenizationData } from './hooks/useTokenizationData';

type Props = {
	token?: string | null;
	accountId?: string | null;
	paymentType: PaymentType;
};

type Result = { standalonePage: true } | undefined;

export function usePPMU(): (props: Props) => Promise<Result> {
	const isEligibleForMembership = useIsMembershipEligible();

	const { user } = usePromoteFormData();
	const isGuestEmailExistingMember = useIsGuestEmailExistingMember();

	const getMembershipAmount = useEnforceMembershipAmount();
	const getTokenizationData = useTokenizationData();
	const navigateToPpmuPage = useNavigateToPpmuPage();

	return async function processPPMU({ token, accountId, paymentType }): Promise<Result> {
		if (!isEligibleForMembership) return undefined;

		if (await isGuestEmailExistingMember(user.email)) return undefined;

		const { amount } = getMembershipAmount();

		const tokenizationData = getTokenizationData({ paymentType, token, accountId });
		const navigated = await navigateToPpmuPage({
			token: tokenizationData?.token,
			accountId: tokenizationData?.accountId ?? null,
			paymentType,
			tokenType: tokenizationData?.tokenType ?? 'temporary',
			membershipAmount: amount,
		});
		if (navigated) return { standalonePage: true };

		return undefined;
	};
}
